import { supabase } from '@/lib/supabase/client';
import type { Invoice, InvoiceInsert, InvoiceUpdate } from '@/types/invoices';
import { normalizeAgreementTerms } from '@/types/agreement-terms';
import { normalizeInvoiceAgreementTerms } from '@/lib/sla/normalize';
import { suggestDuplicateDocumentNumber } from '@/lib/utils/sales-document-numbers';

// Helper to get current user ID
async function getCurrentUserId(): Promise<string | null> {
  const {
    data: { user },
  } = await supabase.auth.getUser();
  return user?.id ?? null;
}

function isMissingInvoiceLocationIdColumnError(error: { message?: string | null } | null): boolean {
  const message = error?.message || '';
  return message.includes("'location_id'") && message.includes("'invoices'");
}

export const invoicesService = {
  async getAll(): Promise<Invoice[]> {
    try {
      const { data, error } = await supabase.from('invoices').select('*').order('created_at', { ascending: false });

      if (error) {
        console.error('Error fetching invoices:', error);
        return [];
      }

      // Parse line_items JSONB
      return (data as any[])
        .map((invoice) => ({
          ...invoice,
          line_items: typeof invoice.line_items === 'string' ? JSON.parse(invoice.line_items) : invoice.line_items,
          agreement_terms: invoice.agreement_terms_json ?? null,
        }))
        .map((invoice) => normalizeInvoiceAgreementTerms(invoice as Invoice));
    } catch (error) {
      console.error('Error fetching invoices:', error);
      return [];
    }
  },

  async getById(id: string): Promise<Invoice | null> {
    try {
      const { data, error } = await supabase.from('invoices').select('*').eq('id', id).single();

      if (error) {
        if (error.code === 'PGRST116') {
          return null;
        }
        console.error('Error fetching invoice:', error);
        return null;
      }

      if (!data) {
        return null;
      }

      // Parse line_items JSONB
      return normalizeInvoiceAgreementTerms({
        ...data,
        line_items: typeof data.line_items === 'string' ? JSON.parse(data.line_items) : data.line_items,
        agreement_terms: (data as any).agreement_terms_json ?? null,
      } as Invoice);
    } catch (error) {
      console.error('Error fetching invoice:', error);
      return null;
    }
  },

  async getByDealId(dealId: string): Promise<Invoice[]> {
    try {
      const { data, error } = await supabase
        .from('invoices')
        .select('*')
        .eq('deal_id', dealId)
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Error fetching invoices by deal ID:', error);
        return [];
      }

      // Parse line_items JSONB
      return (data as any[])
        .map((invoice) => ({
          ...invoice,
          line_items: typeof invoice.line_items === 'string' ? JSON.parse(invoice.line_items) : invoice.line_items,
          agreement_terms: invoice.agreement_terms_json ?? null,
        }))
        .map((invoice) => normalizeInvoiceAgreementTerms(invoice as Invoice));
    } catch (error) {
      console.error('Error fetching invoices by deal ID:', error);
      return [];
    }
  },

  async getByLocationOrderId(locationOrderId: string): Promise<Invoice[]> {
    try {
      const { data, error } = await supabase
        .from('invoices')
        .select('*')
        .eq('location_order_id', locationOrderId)
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Error fetching invoices by location order ID:', error);
        return [];
      }

      // Parse line_items JSONB
      return (data as any[])
        .map((invoice) => ({
          ...invoice,
          line_items: typeof invoice.line_items === 'string' ? JSON.parse(invoice.line_items) : invoice.line_items,
          agreement_terms: invoice.agreement_terms_json ?? null,
        }))
        .map((invoice) => normalizeInvoiceAgreementTerms(invoice as Invoice));
    } catch (error) {
      console.error('Error fetching invoices by location order ID:', error);
      return [];
    }
  },

  async getByQuoteId(quoteId: string): Promise<Invoice[]> {
    try {
      const { data, error } = await supabase
        .from('invoices')
        .select('*')
        .eq('quote_id', quoteId)
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Error fetching invoices by quote ID:', error);
        return [];
      }

      return (data as any[])
        .map((invoice) => ({
          ...invoice,
          line_items: typeof invoice.line_items === 'string' ? JSON.parse(invoice.line_items) : invoice.line_items,
          agreement_terms: invoice.agreement_terms_json ?? null,
        }))
        .map((invoice) => normalizeInvoiceAgreementTerms(invoice as Invoice));
    } catch (error) {
      console.error('Error fetching invoices by quote ID:', error);
      return [];
    }
  },

  async create(data: InvoiceInsert): Promise<Invoice> {
    try {
      const userId = await getCurrentUserId();

      const insertData: Record<string, unknown> = {
        location_id: data.location_id ?? null,
        deal_id: data.deal_id ?? null,
        quote_id: data.quote_id ?? null,
        location_order_id: data.location_order_id ?? null,
        invoice_number: data.invoice_number,
        status: data.status ?? 'draft',
        provisions_new_location: data.provisions_new_location ?? false,
        due_date: data.due_date ?? null,
        customer_name: data.customer_name,
        line_items: data.line_items,
        subtotal: data.subtotal ?? 0,
        tax: data.tax ?? 0,
        total: data.total ?? 0,
        notes: data.notes ?? null,
        agreement_terms_json: normalizeAgreementTerms(data.agreement_terms),
        created_by: userId,
      };

      let { data: newInvoice, error } = await supabase.from('invoices').insert(insertData).select().single();

      if (error && isMissingInvoiceLocationIdColumnError(error)) {
        console.warn('invoices.location_id column not found, retrying create without it');
        const legacyInsertData = { ...insertData };
        delete legacyInsertData.location_id;

        const retryResult = await supabase.from('invoices').insert(legacyInsertData).select().single();
        newInvoice = retryResult.data;
        error = retryResult.error;
      }

      if (error) {
        console.error('Error creating invoice:', error);
        throw new Error(`Failed to create invoice: ${error.message}`);
      }

      // Parse line_items JSONB
      return normalizeInvoiceAgreementTerms({
        ...newInvoice,
        line_items: typeof newInvoice.line_items === 'string' ? JSON.parse(newInvoice.line_items) : newInvoice.line_items,
        agreement_terms: (newInvoice as any).agreement_terms_json ?? null,
      } as Invoice);
    } catch (error) {
      console.error('Error creating invoice:', error);
      throw error;
    }
  },

  async update(id: string, data: InvoiceUpdate): Promise<Invoice> {
    try {
      const updateData: Record<string, unknown> = {};

      // Only include fields that are provided
      if (data.location_id !== undefined) updateData.location_id = data.location_id;
      if (data.deal_id !== undefined) updateData.deal_id = data.deal_id;
      if (data.quote_id !== undefined) updateData.quote_id = data.quote_id;
      if (data.location_order_id !== undefined) updateData.location_order_id = data.location_order_id;
      if (data.invoice_number !== undefined) updateData.invoice_number = data.invoice_number;
      if (data.status !== undefined) updateData.status = data.status;
      if (data.provisions_new_location !== undefined) {
        updateData.provisions_new_location = data.provisions_new_location;
      }
      if (data.due_date !== undefined) updateData.due_date = data.due_date;
      if (data.customer_name !== undefined) updateData.customer_name = data.customer_name;
      if (data.line_items !== undefined) updateData.line_items = data.line_items;
      if (data.subtotal !== undefined) updateData.subtotal = data.subtotal;
      if (data.tax !== undefined) updateData.tax = data.tax;
      if (data.total !== undefined) updateData.total = data.total;
      if (data.notes !== undefined) updateData.notes = data.notes;
      if (data.agreement_terms !== undefined) updateData.agreement_terms_json = normalizeAgreementTerms(data.agreement_terms);

      let { data: updatedInvoice, error } = await supabase
        .from('invoices')
        .update(updateData)
        .eq('id', id)
        .select()
        .single();

      if (error && isMissingInvoiceLocationIdColumnError(error) && updateData.location_id !== undefined) {
        console.warn('invoices.location_id column not found, retrying update without it');
        const legacyUpdateData = { ...updateData };
        delete legacyUpdateData.location_id;

        const retryResult = await supabase
          .from('invoices')
          .update(legacyUpdateData)
          .eq('id', id)
          .select()
          .single();

        updatedInvoice = retryResult.data;
        error = retryResult.error;
      }

      if (error) {
        console.error('Error updating invoice:', error);
        throw new Error(`Failed to update invoice: ${error.message}`);
      }

      if (!updatedInvoice) {
        throw new Error('Invoice not found');
      }

      // Parse line_items JSONB
      return normalizeInvoiceAgreementTerms({
        ...updatedInvoice,
        line_items: typeof updatedInvoice.line_items === 'string' ? JSON.parse(updatedInvoice.line_items) : updatedInvoice.line_items,
        agreement_terms: (updatedInvoice as any).agreement_terms_json ?? null,
      } as Invoice);
    } catch (error) {
      console.error('Error updating invoice:', error);
      throw error;
    }
  },

  async setStatus(id: string, status: Invoice['status']): Promise<Invoice> {
    try {
      const { data: updatedInvoice, error } = await supabase
        .from('invoices')
        .update({ status })
        .eq('id', id)
        .select()
        .single();

      if (error) {
        console.error('Error updating invoice status:', error);
        throw new Error(`Failed to update invoice status: ${error.message}`);
      }

      if (!updatedInvoice) {
        throw new Error('Invoice not found');
      }

      // Parse line_items JSONB
      return normalizeInvoiceAgreementTerms({
        ...updatedInvoice,
        line_items: typeof updatedInvoice.line_items === 'string' ? JSON.parse(updatedInvoice.line_items) : updatedInvoice.line_items,
        agreement_terms: (updatedInvoice as any).agreement_terms_json ?? null,
      } as Invoice);
    } catch (error) {
      console.error('Error updating invoice status:', error);
      throw error;
    }
  },

  async duplicate(
    sourceInvoiceId: string,
    overrides?: { invoice_number?: string; customer_name?: string | null; location_id?: string | null; deal_id?: string | null }
  ): Promise<Invoice> {
    const source = await this.getById(sourceInvoiceId);
    if (!source) {
      throw new Error('Invoice not found');
    }

    const allInvoices = await this.getAll();
    const invoiceNumber =
      overrides?.invoice_number?.trim() ||
      suggestDuplicateDocumentNumber(
        source.invoice_number,
        allInvoices.map((invoice) => invoice.invoice_number)
      );

    const duplicatedNotes = source.notes
      ? `${source.notes}\n\nDuplicated from ${source.invoice_number}.`
      : `Duplicated from ${source.invoice_number}.`;

    return this.create({
      location_id: overrides?.location_id !== undefined ? overrides.location_id : null,
      deal_id: overrides?.deal_id !== undefined ? overrides.deal_id : null,
      quote_id: null,
      location_order_id: null,
      invoice_number: invoiceNumber,
      customer_name: overrides?.customer_name ?? '',
      status: 'draft',
      provisions_new_location: source.provisions_new_location ?? false,
      due_date: source.due_date,
      line_items: source.line_items.map((item) => ({ ...item })),
      subtotal: source.subtotal,
      tax: source.tax,
      total: source.total,
      notes: duplicatedNotes,
      agreement_terms: source.agreement_terms,
    });
  },

  async delete(id: string): Promise<void> {
    try {
      const invoice = await this.getById(id);
      const { error } = await supabase.from('invoices').delete().eq('id', id);

      if (error) {
        console.error('Error deleting invoice:', error);
        throw new Error(`Failed to delete invoice: ${error.message}`);
      }

      if (invoice?.quote_id) {
        const remainingInvoices = await this.getByQuoteId(invoice.quote_id);
        if (remainingInvoices.length === 0) {
          const { data: linkedQuote, error: quoteError } = await supabase
            .from('quotes')
            .select('id, status')
            .eq('id', invoice.quote_id)
            .maybeSingle();

          if (quoteError) {
            console.error('Error fetching linked quote while deleting invoice:', quoteError);
            throw new Error(`Failed to update linked quote: ${quoteError.message}`);
          }

          if (linkedQuote?.status === 'converted') {
            const { error: updateQuoteError } = await supabase
              .from('quotes')
              .update({ status: 'accepted' })
              .eq('id', invoice.quote_id);

            if (updateQuoteError) {
              console.error('Error restoring quote status while deleting invoice:', updateQuoteError);
              throw new Error(`Failed to update linked quote: ${updateQuoteError.message}`);
            }
          }
        }
      }
    } catch (error) {
      console.error('Error deleting invoice:', error);
      throw error;
    }
  },
};
